import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) return setError("Please enter your email and password");
    setLoading(true);
    try {
      const r = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Login failed");
      const devOtp = data.dev_otp ? `&dev_otp=${data.dev_otp}` : "";
      nav(`/verify-otp?email=${encodeURIComponent(data.email)}&purpose=${data.purpose}&from=${encodeURIComponent(loc.state?.from || "/events")}${devOtp}`);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src="/mc-crest.png"
            alt="Province of Middlesex crest"
            className="mx-auto mb-4 h-20 w-auto"
            data-testid="brand-crest"
          />
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Province of Middlesex</div>
          <h1 className="text-3xl font-semibold mt-1">Middlesex Connect</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Members Events and Bookings — in one trusted place.
          </p>
        </div>
        <Card className="border-border">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Sign in</h2>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full">
                {loading ? "Please wait…" : "Continue"}
              </Button>
              <div className="text-center">
                <Link
                  to={`/reset-password?email=${encodeURIComponent(email.trim().toLowerCase())}`}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase tracking-widest">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => nav("/register")}
                data-testid="goto-register"
                className="w-full"
              >
                Create a member account
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="text-center text-xs text-muted-foreground mt-6">Middlesex Connect · v2.1.1</div>
      </div>
    </div>
  );
}
